<template>
    <v-container class="py-2">
        <v-layout column>
            <v-flex class="font-weight-bold">
                Payment plan set up for:
            </v-flex>
            <v-flex class="mt-2 text-h6" data-testid="unit-name">Unit {{ unitName }}</v-flex>
            <v-btn
                data-testid="ar-unit-select-btn"
                class="ar-unit-select-btn"
                v-if="!invoiceStep"
                icon
                absolute
                style="left: 14.5rem; top: 5.75rem"
                @click="toUnitSelect"
            >
                <v-icon small>edit</v-icon>
            </v-btn>
        </v-layout>
        <v-layout column class="mt-4">
            <v-flex class="font-weight-bold">Payment(s) scheduled for:</v-flex>
            <!-- For Payment Plan -->
            <v-flex class="mt-2 text-h6" data-testid="payment-schedule" v-if="!promiseToPay">{{ paymentSchedule }}</v-flex>
            <!-- For Promise To Pay -->
            <v-flex class="mt-2 text-h6" v-else data-testid="promise-unit-list-payments">{{ promiseSchedule }}</v-flex>
        </v-layout>
        <v-layout column class="mt-4">
            <v-flex class="font-weight-bold">
                <span>Payment date scheduled:
                    <v-btn
                        data-testid="promise-date-select-btn"
                        class="promise-date-select-btn ml-3"
                        v-if="!invoiceStep && promiseToPay"
                        icon
                        absolute
                        @click="toPromiseDate"
                        style="margin-top: -10px"
                    >
                        <v-icon small>edit</v-icon>
                    </v-btn>
                </span>
            </v-flex>
            <v-flex class="mt-2">
                <span class="text-h6" data-testid="payment-date">{{ paymentDate }}</span>
                <span class="ml-3 font-weight-bold" v-if="!promiseToPay">
                    Payment attempts will be made on the first, third and fifth days of the month.
                </span>
            </v-flex>
            <v-flex class="text-caption mt-1 font-weight-bold" v-if="!promiseToPay">*Automatic payments enabled</v-flex>
        </v-layout>
        <div class="mt-4 text-h6 font-weight-bold">Tenant Info:</div>
        <v-layout column class="mt-3">
            <v-flex class="font-weight-bold">Name</v-flex>
            <v-flex class="mt-1 subheading font-weight-bold" data-testid="tenant-name">
                {{ info.first_name }} {{ info.last_name }}
            </v-flex>
        </v-layout>
        <v-layout class="mt-2">
            <div>
                <div class="font-weight-bold">Address</div>
                <div class="mt-1 subheading font-weight-bold" data-testid="tenant-address">{{ info.street_address }}</div>
            </div>
            <div class="mx-3">
                <div class="font-weight-bold">City</div>
                <div class="mt-1 subheading font-weight-bold" data-testid="tenant-city"> {{ info.city }}</div>
            </div>
            <div class="mx-3">
                <div class="font-weight-bold">State</div>
                <div class="mt-1 subheading font-weight-bold" data-testid="tenant-state">{{ info.state }}</div>
            </div>
            <div class="mx-3">
                <div class="font-weight-bold">Zipcode</div>
                <div class="mt-1 subheading font-weight-bold" data-testid="tenant-zipcode">{{ info.postal_code }}</div>
            </div>
        </v-layout>
        <div v-if="invoiceStep" data-testid="payment-summary">
            <v-layout class="mt-5">
                <v-flex class="font-weight-bold">
                    Email with payment plan information and secure link for payment options sent to: {{ info.email }}.
                </v-flex>
            </v-layout>
            <v-divider class="mt-5 mb-3" />
            <v-layout>
                <v-flex xs9 />
                <!-- For Payment Plan -->
                <v-flex xs3 v-if="!promiseToPay">
                    <v-layout
                        class="mb-2"
                        v-if="selectedPaymentPlan"
                    >
                        <v-flex xs7 data-testid="payment-unit-name">
                            Unit {{ selectedPaymentPlan.unit_name }}:
                        </v-flex>
                        <v-flex
                            xs2
                            class="text-right"
                            data-testid="unit-subtotal"
                        >
                            ${{ parseFloat(this.paymentTotalDue).toFixed(2) }}
                        </v-flex>
                    </v-layout>
                </v-flex>
                <!-- For Promise To Pay -->
                <v-flex xs3 v-else>
                    <v-layout
                        class="mb-2"
                        v-if="selectedPromiseToPay"
                    >
                        <v-flex xs7 data-testid="promise-unit-name">Unit {{ unitName }}:</v-flex>
                        <v-flex
                            xs2
                            class="text-right"
                            data-testid="unit-subtotal"
                        >
                            ${{ parseFloat(this.promiseTotalDue).toFixed(2) }}
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
            <v-divider class="mt-2" />
            <v-layout>
                <v-flex xs9 class="text-h5 mt-3 font-weight-bold">Total</v-flex>
                <v-layout class="mt-3 font-weight-bold">
                    <v-flex xs7>
                        Non-refundable Total Charges:
                    </v-flex>
                    <v-flex
                        xs2
                        class="text-right"
                        data-testid="total-charges"
                    >
                        <span v-if="!promiseToPay">${{ parseFloat(this.paymentTotalDue).toFixed(2) }}</span>
                        <span v-else>${{ parseFloat(this.promiseTotalDue).toFixed(2) }}</span>
                    </v-flex>
                </v-layout>
            </v-layout>
        </div>
    </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import moment from "moment";

export default {
  name: "UnitPlanSummary",

  props: {
    invoiceStep: {
      type: Boolean
    }
  },

  methods: {
    toUnitSelect() {
      this.$store.commit("setCurrentStep", 1);
    },

    toPromiseDate() {
      this.$store.commit("setCurrentStep", 2);
    },

    formatDate(date) {
      return new moment(date).format("MM/DD/YYYY");
    }
  },
  computed: {
    ...mapState({
      info: state => state.tenant.info,
      planMonths: state => state.accountsReceivable.planMonths,
      promiseMonths: state => state.accountsReceivable.promiseMonths,
      promiseDate: state => state.accountsReceivable.promiseDate
    }),

    ...mapGetters({
      selectedPaymentPlan: "selectedPaymentPlan",
      selectedPromiseToPay: "selectedPromiseToPay"
    }),

    promiseToPay() {
      return this.$route.name === "promiseToPay";
    },

    paymentDate() {
      if (!this.promiseToPay) {
        const paymentPlanDate = moment.utc().add(1, "month").startOf("month");
        // commit this date to the store for access for createPaymentPlan payload
        const formattedPlanDate = paymentPlanDate.format("YYYY-MM-DD");
        this.$store.commit("updatePlanDate", formattedPlanDate);
        // Full display of date on Payment Review/Invoice
        return paymentPlanDate.format("dddd, MMMM Do YYYY");
      } else {
        return this.formatDate(this.promiseDate);
      }
    },

    paymentSchedule() {
      if (this.selectedPaymentPlan) {
        const monthsAmount = this.planMonths === "2" ? "Two" : "Three";
        return "Next " + monthsAmount + " Months ($" + this.selectedPaymentPlan.total_due + ")";
      } else {
        return "";
      }
    },

    paymentTotalDue() {
      return this.selectedPaymentPlan ? this.selectedPaymentPlan.total_due : "";
    },

    promiseSchedule() {
      if (this.selectedPromiseToPay) {
        const monthsAmount = this.promiseMonths === "1" ? "Current Month" : "Current + Next Month";
        return monthsAmount + " ($" + this.promiseTotalDue.toFixed(2) + ")";
      } else {
        return "";
      }
    },

    promiseTotalDue() {
      return this.selectedPromiseToPay !== undefined ? this.selectedPromiseToPay.total_due : "";
    },

    unitName() {
      if (!this.selectedPaymentPlan) {
        return "";
      }
      if (!this.promiseToPay) {
        return this.selectedPaymentPlan.unit_name !== undefined ? this.selectedPaymentPlan.unit_name : "";
      } else {
        return this.selectedPromiseToPay !== undefined ? this.selectedPromiseToPay.unit_name : "";
      }
    }
  }
};
</script>
